import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveWizardForm as saveWizardFormApi } from 'services/product';

import { CustomNotification } from 'common/components';
import {
  transformWizardItems,
  getQuestionParams,
  autoPopulateCaseGtin,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/wizardProduct';

import { wizardFormKeys } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/queryKeys';
import useWizard from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/context/useWizard';

export const useSaveWizardForm = (options = {}) => {
  const queryClient = useQueryClient();

  const { actions } = useWizard();

  const {
    mutate: saveWizardFormMutation,
    mutateAsync,
    isLoading: isSaving,
  } = useMutation({
    mutationFn: (params) => saveWizardFormApi(params),
    onSuccess: (data, variables, context) => {
      if (data?.data?.wizardFormId) {
        queryClient.invalidateQueries({
          queryKey: wizardFormKeys.detail(data?.wizardFormId),
        });
      }

      queryClient.invalidateQueries({
        queryKey: wizardFormKeys.pending(),
      });

      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      if (error?.response?.data?.message) {
        CustomNotification.error(error.response.data.message);
      } else {
        CustomNotification.error('Failed to save form');
      }

      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
    onSettled: (data, error, variables, context) => {
      if (options.onSettled) {
        options.onSettled(data, error, variables, context);
      }
    },
    ...options,
  });

  const saveWizardForm = useCallback(
    ({ state, memberId }) => {
      // Auto-populate caseGtin first so state + grid reflect the value immediately
      const populatedProducts = (state?.products ?? []).map(autoPopulateCaseGtin);
      actions.setProducts(populatedProducts);

      const requestPayload = {
        ...getQuestionParams(state),
        memberId,
        wizardFormId: state.wizardFormId,
        wizardFormItems: transformWizardItems(populatedProducts),
      };

      saveWizardFormMutation(requestPayload, {
        onSuccess: (response) => {
          if (response?.isSuccess) {
            CustomNotification.success('Form saved successfully');

            if (response?.data?.wizardFormId) {
              actions.setWizardFormId(response?.data?.wizardFormId);
            }
          } else {
            CustomNotification.error(
              response?.data?.message ?? 'Failed to save form'
            );
          }
        },
      });
    },
    [actions, saveWizardFormMutation]
  );

  return {
    saveWizardForm,
    isSaving,
    mutate: saveWizardFormMutation,
    saveWizardFormAsync: mutateAsync,
  };
};
